<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-paper-container">
    <div class="orangehrm-header-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('expensa.menu_approvals') }}
      </oxd-text>
    </div>
    <oxd-divider />
    <table-header :total="total" :loading="isLoading" />
    <oxd-text
      v-if="state === 'error'"
      tag="p"
      class="orangehrm-container expensa-empty"
    >
      {{ $t('expensa.list_error') }}
    </oxd-text>
    <oxd-text
      v-else-if="state === 'empty'"
      tag="p"
      class="orangehrm-container expensa-empty"
    >
      {{ $t('expensa.approvals_empty') }}
    </oxd-text>
    <div v-else class="orangehrm-container">
      <oxd-card-table
        :items="items.data || []"
        :headers="headers"
        :selectable="false"
        :clickable="true"
        :loading="isLoading"
        row-decorator="oxd-table-decorator-card"
        @click="onClickItem"
      />
    </div>
    <div class="orangehrm-bottom-container">
      <oxd-pagination
        v-if="showPaginator"
        v-model:current="currentPage"
        :length="pages"
      />
    </div>
  </div>
</template>

<script>
import {computed, markRaw} from 'vue';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import useEmployeeNameTranslate from '@/core/util/composable/useEmployeeNameTranslate';
import usei18n from '@/core/util/composable/usei18n';
import StatusPill from '@/orangehrmExpensaPlugin/components/StatusPill.vue';
import {expensaApi, pageUrl} from '../util/api';
import {eur, instant} from '../util/format';
import {reportStatusKey} from '../util/labels';
import {listState} from '../util/listState';

export default {
  name: 'ExpensaApprovals',

  setup() {
    const {$t} = usei18n();
    const {$tEmpName} = useEmployeeNameTranslate();

    const normalizer = (data) =>
      data.map((r) => ({
        id: r.id,
        owner: $tEmpName(r.owner),
        title: r.title,
        status: $t(reportStatusKey(r.status, r.awaitingFinance)),
        statusTone: r.awaitingFinance ? 'AWAITING' : r.status,
        total: eur(r.totalEur),
        submittedAt: instant(r.submittedAt),
      }));

    // Solo le note che l'utente può approvare: lo decide il server (toApprove).
    const query = computed(() => ({toApprove: true}));

    const {showPaginator, currentPage, total, pages, response, isLoading} =
      usePaginate(expensaApi('/reports'), {
        normalizer,
        query,
        toastNoRecords: false,
      });

    return {
      showPaginator,
      currentPage,
      total,
      pages,
      items: response,
      isLoading,
    };
  },

  data() {
    return {
      headers: [
        {
          name: 'owner',
          title: this.$t('expensa.col_employee'),
          style: {flex: 2},
        },
        {
          name: 'title',
          title: this.$t('expensa.col_title'),
          cellType: 'oxd-table-cell-truncate',
          style: {flex: 3},
        },
        {
          name: 'status',
          title: this.$t('expensa.col_status'),
          style: {flex: 2},
          cellRenderer: this.statusRenderer,
        },
        {
          name: 'total',
          title: this.$t('expensa.col_total'),
          style: {flex: 1},
        },
        {
          name: 'submittedAt',
          title: this.$t('expensa.col_submitted'),
          style: {flex: 2},
        },
      ],
    };
  },

  computed: {
    state() {
      return listState(this.isLoading, this.items, this.total);
    },
  },

  methods: {
    statusRenderer(...[, , , row]) {
      return {
        component: markRaw(StatusPill),
        props: {label: row.status, tone: row.statusTone},
      };
    },
    onClickItem(item) {
      window.location.href = pageUrl('viewReport', item.id);
    },
  },
};
</script>

<style scoped lang="scss">
.expensa-empty {
  padding: 0 1.2rem 1.2rem;
}
</style>
